import { isEqual } from 'lodash';

/**
 * Pure logic for the EDUS-2528 follow-up: deciding whether a filter-bar
 * change should preserve the user's cross-page selection or clear it.
 *
 * Original behavior: `CdwSsrmGridComponent.onFilterBarChange` cleared
 * selection on EVERY filter change. Justification was that previously-
 * selected rows might no longer match the new filter, leaving the user
 * with an "ambiguous bulk-action scope" (could act on rows they can't
 * see). That argument only holds when the filter becomes more
 * restrictive — when the user REMOVES or widens a filter, the
 * previously-selected rows are still in scope and clearing destroys
 * their work.
 *
 * Extracted out of the component file so it can be unit-tested without
 * importing the AG-Grid runtime (same constraint as
 * `selection-lock-revert.ts` and `sql-ssrm-datasource.factory.ts` —
 * see their headers for the vitest/ESM rationale).
 *
 * Uses structural typing so this module has no dependency on
 * filter-bar-interfaces. `FilterLineLike` is satisfied by both
 * `SelectedFilterField` (filter-bar-interfaces) and `CdwGridFilter`
 * (common-data) at the call site.
 */

/**
 * Minimum structural shape this helper reads off a filter-bar entry.
 * Anything the helper doesn't read (`uid`, full matcher metadata, etc.)
 * is intentionally absent — keeping the interface narrow makes the
 * cast at the call site trivially safe.
 */
export interface FilterLineLike {
  readonly field: { readonly name: string };
  readonly selectedMatcher: { readonly name: string };
  readonly matchValue: unknown;
}

/**
 * True iff every filter line in `next` is structurally identical to a
 * line in `prev` — i.e., `next` is a (non-strict) subset of `prev` by
 * line. Equivalent to "every constraint the user keeps was already
 * there" → the new filter view is at least as permissive as the old
 * one, so previously-matched rows still match.
 *
 * Returns `true` for:
 *   - `next` empty (no constraints → most permissive possible)
 *   - `next` is a structural subset of `prev` (one or more lines removed)
 *   - `next === prev` line-for-line (no-op filter change, e.g., focus blur)
 *
 * Returns `false` for:
 *   - `next` adds a line not present in `prev` (new constraint added)
 *   - `next` swaps a matcher on an existing field (e.g., `equals` →
 *     `contains` for the same field name) — semantically often a
 *     widening but indistinguishable structurally
 *   - `next` changes a `matchValue` while keeping the same field +
 *     matcher (could be widening or narrowing — conservative clear)
 *
 * The structural check is deliberately stricter than full semantic
 * permissiveness. False-negatives (clearing when we could preserve)
 * just match the old "always clear" behavior in those edge cases.
 * False-positives (preserving when we shouldn't) would be the
 * dangerous direction — they'd let users bulk-act on hidden rows —
 * and the structural check makes them impossible: if a line appears
 * in `next` it MUST also appear in `prev`.
 *
 * Equality is delegated to `lodash.isEqual` on the three fields we
 * actually read (`field.name`, `selectedMatcher.name`, `matchValue`).
 * Filter counts in practice are small (typically 1-10 lines), so the
 * O(n·m) per-line scan is irrelevant in absolute terms. Matches the
 * established `isEqual`-on-filter-arrays pattern used elsewhere in
 * the grid stack (`cdw-search-grid.component.ts`, `device-search
 * .component.ts`, `report-viewer.component.ts`).
 */
export function isFilterChangePermissive(
  prev: readonly FilterLineLike[],
  next: readonly FilterLineLike[]
): boolean {
  if (next.length === 0) return true;
  return next.every((nextLine) =>
    prev.some((prevLine) => filterLinesEqual(prevLine, nextLine))
  );
}

/**
 * Structural equality on the three fields the permissiveness check
 * reads. Defined as a small helper so the `every`/`some` body stays
 * readable and so callers can't accidentally extend the equality
 * surface by adding fields to `FilterLineLike`.
 */
function filterLinesEqual(a: FilterLineLike, b: FilterLineLike): boolean {
  return (
    a.field.name === b.field.name &&
    a.selectedMatcher.name === b.selectedMatcher.name &&
    isEqual(a.matchValue, b.matchValue)
  );
}
